/* eslint-disable react-hooks/exhaustive-deps */
import { IOption } from 'components/select/multiSelect/dropdownSelect/DropdownSelect';
import { useContextPrevision } from 'pages/admin/demand/planning/context/ContextPrevision';
import {
	planningTabsFilterCompletedRequest,
	planningTabsFilterLabelsRequest,
} from 'pages/admin/demand/planning/requests/planningTabs/tabFilter';
import { useCallback, useEffect, useRef, useState } from 'react';

import { IFilterHierarchy } from '../types/planning.types';
import {
	getParentLevel,
	HIERARCHY_MAPPING,
	IHierarchySelections,
	LEVEL_MAPPING,
} from '../variables/PlanningByLevelMappings';

interface IHierarchyData {
	data: IOption[];
	isLoading: boolean;
	isLoadingMore: boolean;
	isLoadingCompleted: boolean;
	hasNextPage: boolean;
	currentPage: number;
	searchTerm: string;
	total: number;
}

interface IUseMultiHierarchyDataParams {
	cycleId: number;
	hierarchySelections: IHierarchySelections;
	filter?: IFilterHierarchy;
}

export const useMultiHierarchyData = ({
	cycleId,
	hierarchySelections,
	filter,
}: IUseMultiHierarchyDataParams) => {
	const [hierarchiesData, setHierarchiesData] = useState<
		Record<string, IHierarchyData>
	>({});
	const abortControllersRef = useRef<Record<string, AbortController>>({});
	const abortControllersCompletedRef = useRef<
		Record<string, AbortController>
	>({});
	const searchTimeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});
	const previousSearchTermsRef = useRef<Record<string, string>>({});
	const lastSearchResultsRef = useRef<Record<string, IOption[]>>({});
	const { planningType, companyAreaId } = useContextPrevision();

	const initializeHierarchyData = useCallback((): IHierarchyData => {
		return {
			data: [],
			isLoading: false,
			isLoadingMore: false,
			isLoadingCompleted: false,
			hasNextPage: false,
			currentPage: 1,
			searchTerm: '',
			total: 0,
		};
	}, []);

	const getHierarchyData = useCallback(
		(level: string): IHierarchyData => {
			if (!hierarchiesData[level]) {
				const initialData = initializeHierarchyData();
				setHierarchiesData(prev => ({
					...prev,
					[level]: initialData,
				}));
				return initialData;
			}
			return hierarchiesData[level];
		},
		[hierarchiesData, initializeHierarchyData]
	);
	const fetchHierarchyData = useCallback(
		async (
			level: string,
			page: number = 1,
			search: string = '',
			append: boolean = false
		) => {
			const apiLevel = LEVEL_MAPPING[level];
			const hierarchy = HIERARCHY_MAPPING[level];

			abortControllersRef.current[level]?.abort();
			abortControllersRef.current[level] = new AbortController();

			const isSearch = search !== '';
			const effectiveSize = isSearch ? 50 : 20;

			try {
				setHierarchiesData(prev => ({
					...prev,
					[level]: {
						...prev[level],
						isLoading: !append,
						isLoadingMore: append,
						isLoadingCompleted: true,
					},
				}));

				let filterByParent:
					| Array<{ level: string; value: number[] }>
					| undefined = [];
				let currentLevel = level;
				while (true) {
					const parentLevel = getParentLevel(currentLevel as any);
					if (!parentLevel) break;
					const parentSelections =
						hierarchySelections[parentLevel] || [];
					if (parentSelections.length > 0) {
						const parentApiLevel =
							LEVEL_MAPPING[parentLevel as any];
						const valuesArray = parentSelections.map(sel =>
							Number(sel.value)
						);
						filterByParent.push({
							level: parentApiLevel,
							value: valuesArray,
						});
					}
					currentLevel = parentLevel;
				}
				if (filterByParent.length === 0) filterByParent = undefined;

				const requestParams = {
					page: isSearch ? 1 : page,
					size: effectiveSize,
					order: [['label', 'ASC']] as [string, 'ASC' | 'DESC'][],
					hierarchy,
					level: apiLevel,
					search,
					cycle_id: cycleId,
					company_area_id: companyAreaId,
					...(filterByParent
						? { filter_by_parent: filterByParent }
						: {}),
					filters_check: {
						product_ids: filter?.productIds || [],
						commercial_hierarchy_ids: filter?.commercialIds || [],
						customer_ids: filter?.customerIds || [],
					},
				};

				const labelsResponse = await planningTabsFilterLabelsRequest(
					planningType,
					requestParams
				);

				if (abortControllersRef.current[level]?.signal.aborted) return;

				const options: IOption[] = labelsResponse.rows.map(item => ({
					id: item.value,
					value: item.value,
					label: item.label,
					completed: 'none' as const,
					completedLoading: true,
				}));

				if (search !== '') {
					lastSearchResultsRef.current[level] = options;
				}

				setHierarchiesData(prev => ({
					...prev,
					[level]: {
						...prev[level],
						data: append
							? [...(prev[level]?.data || []), ...options]
							: options,
						isLoading: false,
						isLoadingMore: false,
						hasNextPage:
							effectiveSize * (isSearch ? 1 : page) <
							labelsResponse.count,
						currentPage: isSearch ? 1 : page,
						searchTerm: search,
						total: labelsResponse.count,
					},
				}));

				abortControllersCompletedRef.current[level]?.abort();
				abortControllersCompletedRef.current[level] =
					new AbortController();

				try {
					const completedResponse =
						await planningTabsFilterCompletedRequest(
							planningType,
							requestParams
						);

					if (
						abortControllersCompletedRef.current[level]?.signal
							.aborted
					)
						return;

					const completedMap = new Map<
						number,
						'manual' | 'automatic' | 'none'
					>();
					completedResponse.rows.forEach(item => {
						if (item.completed) {
							completedMap.set(item.value, item.completed);
						}
					});

					setHierarchiesData(prev => {
						const currentData = prev[level]?.data || [];
						const updatedData = currentData.map(option => ({
							...option,
							completed:
								completedMap.get(Number(option.value)) ||
								option.completed ||
								'none',
							completedLoading: false,
						}));

						return {
							...prev,
							[level]: {
								...prev[level],
								data: updatedData,
								isLoadingCompleted: false,
							},
						};
					});
				} catch (completedError) {
					if (
						completedError instanceof Error &&
						completedError.name !== 'AbortError'
					) {
						setHierarchiesData(prev => ({
							...prev,
							[level]: {
								...prev[level],
								isLoadingCompleted: false,
							},
						}));
					}
				}
			} catch (error) {
				if (error instanceof Error && error.name !== 'AbortError') {
					setHierarchiesData(prev => ({
						...prev,
						[level]: {
							...prev[level],
							isLoading: false,
							isLoadingMore: false,
							isLoadingCompleted: false,
						},
					}));
				}
			}
		},
		[cycleId, hierarchySelections, filter]
	);

	const handleHierarchyFocusLoad = useCallback(
		(level: string) => {
			const hierarchyData = getHierarchyData(level);

			if (hierarchyData.data.length === 0 && !hierarchyData.isLoading) {
				fetchHierarchyData(level, 1, '', false);
			}
		},
		[getHierarchyData, fetchHierarchyData, hierarchySelections]
	);

	const handleHierarchySearch = useCallback(
		(level: string, searchTerm: string, selectedItems: IOption[] = []) => {
			if (searchTimeoutsRef.current[level]) {
				clearTimeout(searchTimeoutsRef.current[level]);
			}
			if (previousSearchTermsRef.current[level] === searchTerm) {
				return;
			}
			if (searchTerm === '') {
				fetchHierarchyData(level, 1, '', false).then(() => {
					setHierarchiesData(prev => {
						const current = prev[level] || {
							data: [],
							isLoading: false,
							isLoadingMore: false,
							hasNextPage: false,
							currentPage: 1,
							searchTerm: '',
							total: 0,
						};
						const selectedIds = new Set(
							(selectedItems || []).map(i => i.id)
						);
						const selected = (selectedItems || []).filter(
							i => i.id !== undefined
						);
						const rest = (current.data || []).filter(
							i => !selectedIds.has(i.id)
						);
						return {
							...prev,
							[level]: {
								...current,
								data: [...selected, ...rest],
							},
						};
					});
				});
				previousSearchTermsRef.current[level] = '';
				return;
			}
			setHierarchiesData(prev => ({
				...prev,
				[level]: {
					...prev[level],
					isLoading: true,
				},
			}));
			previousSearchTermsRef.current[level] = searchTerm;
			searchTimeoutsRef.current[level] = setTimeout(() => {
				fetchHierarchyData(level, 1, searchTerm, false);
			}, 800);
		},
		[fetchHierarchyData, getHierarchyData]
	);

	const handleHierarchyLoadMore = useCallback(
		async (level: string) => {
			const hierarchyData = getHierarchyData(level);
			if (!hierarchyData.hasNextPage || hierarchyData.isLoadingMore) {
				return;
			}

			await fetchHierarchyData(
				level,
				hierarchyData.currentPage + 1,
				hierarchyData.searchTerm,
				true
			);
		},
		[getHierarchyData, fetchHierarchyData]
	);

	const getOptionsForHierarchy = useCallback(
		(level: string): IOption[] => {
			return getHierarchyData(level).data;
		},
		[getHierarchyData]
	);

	const getHierarchyLoadingState = useCallback(
		(level: string): boolean => {
			return getHierarchyData(level).isLoading;
		},
		[getHierarchyData]
	);

	const getHierarchyLoadingMoreState = useCallback(
		(level: string): boolean => {
			return getHierarchyData(level).isLoadingMore;
		},
		[getHierarchyData]
	);

	const getHierarchyHasNextPage = useCallback(
		(level: string): boolean => {
			return getHierarchyData(level).hasNextPage;
		},
		[getHierarchyData]
	);

	const getHierarchyLoadingCompletedState = useCallback(
		(level: string): boolean => {
			return getHierarchyData(level).isLoadingCompleted;
		},
		[getHierarchyData]
	);

	const resetHierarchyLevelData = useCallback(
		(level: string) => {
			setHierarchiesData(prev => ({
				...prev,
				[level]: initializeHierarchyData(),
			}));
		},
		[initializeHierarchyData]
	);
	const resetAllHierarchiesData = useCallback(() => {
		setHierarchiesData(prev => {
			const reseted: Record<string, IHierarchyData> = {};
			for (const level of Object.keys(prev)) {
				reseted[level] = initializeHierarchyData();
			}
			return reseted;
		});
	}, [initializeHierarchyData]);
	useEffect(() => {
		resetAllHierarchiesData();
	}, [JSON.stringify(filter)]);
	return {
		getOptionsForHierarchy,
		getHierarchyLoadingState,
		getHierarchyLoadingMoreState,
		getHierarchyHasNextPage,
		getHierarchyLoadingCompletedState,
		handleHierarchyFocusLoad,
		handleHierarchySearch,
		handleHierarchyLoadMore,
		resetHierarchyLevelData,
		resetAllHierarchiesData,
	};
};
